import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productAdminService } from '../../services/api';
import Button from '../../components/Button';
import UsageMeter from '../../components/UsageMeter';
import { formatNumber } from '../../utils/helpers';
import styles from './ProductAdmin.module.css';
import ui from './ProductAdminDashboard.module.css';
import t from './AdminTable.module.css';
import { SectionHeading, StatTile } from './AdminWidgets';
import { ActivityIcon, ChartIcon, FileCheckIcon, FileTextIcon, PenIcon } from './ProductAdminIcons';

export default function ProductAdminToken() {
  const { tokenId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    productAdminService.tokenUsage(tokenId).then(r => setReport(r.data.data)).catch(err => {
      if ([401, 403].includes(err.response?.status)) { localStorage.removeItem('productAdminToken'); navigate('/product-admin/login', { replace: true }); }
      else setError(err.response?.data?.error || 'Unable to load token usage');
    }).finally(() => setLoading(false));
  }, [tokenId, navigate]);
  const latestActivity = report?.events?.[0]?.started_at;
  const tokenName = report?.token?.name || `Token #${tokenId}`;

  return (
    <div className={ui.container}>
      <header className={ui.pageHeader}>
        <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>← Back</Button>
        <span className={ui.eyebrow}>TOKEN USAGE</span>
        <h1 className={ui.title} title={tokenName}>{tokenName}</h1>
        <p className={ui.subtitle}>Detailed signing activity for this token.</p>
      </header>

      {error && <div className={styles.error}>{error}</div>}
      {loading && <section className={ui.panel}><p className={styles.loading}>Loading token usage…</p></section>}

      {!loading && report && <>
        <section className={styles.tokenHero}>
          <div>
            <span className={`${ui.eyebrow} ${styles.heroEyebrow}`}>TOKEN PERFORMANCE</span>
            <h2>{report.token.name || `Token #${tokenId}`}</h2>
            <p>Token ID #{report.token.id} · Created {report.token.created_at ? new Date(report.token.created_at).toLocaleDateString() : '—'}</p>
          </div>
          <span className={`${t.badge} ${report.token.status === 'used' ? t.badgeSuccess : t.badgeWarning}`}>{report.token.status}</span>
        </section>

        {report.documents && (
          <section className={ui.panel}>
            <SectionHeading
              tone="teal"
              icon={<FileTextIcon />}
              title="Document Limit"
              text="Documents this licence has signed in its current subscription, out of its share."
            />
            <div className={styles.limitGrid}>
              <div className={styles.limitPanel}>
                <UsageMeter size="lg" label="This licence" used={report.documents.documents_used} limit={report.documents.document_limit} unlimited={report.subscription_documents?.unlimited} caption={report.subscription_documents?.unlimited ? 'No limit until the plan ends' : `${formatNumber(report.documents.documents_remaining)} documents left`} />
              </div>
              {report.subscription_documents && (
                <div className={styles.limitPanel}>
                  <UsageMeter size="lg" label="Whole organization" used={report.subscription_documents.documents_used} limit={report.subscription_documents.document_limit} unlimited={report.subscription_documents.unlimited} caption={report.subscription_documents.is_trial ? 'Free trial' : 'All licences together'} />
                </div>
              )}
            </div>
          </section>
        )}

        <section className={ui.statsGrid}>
          <StatTile tone="blue" title="Files processed" value={formatNumber(report.total_files)}
            hint="PDF files in this token's signing runs" icon={<FileCheckIcon />} />
          <StatTile tone="green" title="Total signatures" value={formatNumber(report.total_signatures)}
            hint="Signature operations completed" icon={<PenIcon />} />
          <StatTile tone="amber" title="Signing runs" value={formatNumber(report.total_events)}
            hint={latestActivity ? `Last used ${new Date(latestActivity).toLocaleDateString()}` : 'No activity yet'} icon={<ActivityIcon />} />
          <StatTile tone="purple" title="Pages scanned" value={formatNumber(report.events.reduce((sum, event) => sum + Number(event.total_pages || 0), 0))}
            hint="Across all tracked documents" icon={<FileTextIcon />} />
        </section>

        <section className={ui.panel}>
          <SectionHeading
            tone="purple"
            icon={<ChartIcon />}
            title="Usage History"
            text="Every signing operation recorded for this token."
            action={<span className={styles.countPill}>{report.total_events} events</span>}
          />
          <div className={t.wrap}>
            <table className={t.table}>
              <thead>
                <tr>
                  <th className={t.wDateTime}>Date</th>
                  <th className={`${t.wStatus} ${t.hideXs}`}>Mode</th>
                  <th className={`${t.wStat} ${t.num} ${t.hideXs}`}>Documents</th>
                  <th className={`${t.wStat} ${t.num} ${t.hideSm}`}>Pages</th>
                  <th className={`${t.wStat} ${t.num}`}>Signatures</th>
                  <th className={t.wStatus}>Status</th>
                  <th className={t.hideSm}>Position</th>
                </tr>
              </thead>
              <tbody>
                {report.events.map(event => {
                  const when = event.started_at ? new Date(event.started_at).toLocaleString() : '—';
                  const position = event.signature_position || 'Custom / manual';
                  return (
                    <tr key={event.event_id}>
                      <td title={when}>{when}</td>
                      <td className={t.hideXs} title={event.mode}>{event.mode}</td>
                      <td className={`${t.num} ${t.hideXs}`} title={String(event.files.length)}>{event.files.length}</td>
                      <td className={`${t.num} ${t.hideSm}`} title={String(event.total_pages)}>{event.total_pages}</td>
                      <td className={`${t.num} ${t.accent}`} title={String(event.signature_count)}>{event.signature_count}</td>
                      <td className={t.fit}>
                        <span className={`${t.badge} ${event.status === 'success' ? t.badgeSuccess : t.badgeWarning}`} title={event.status}>{event.status}</span>
                      </td>
                      <td className={t.hideSm} title={position}>{position}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {!report.events.length && (
              <div className={styles.emptyState}>
                <strong>No usage recorded yet</strong>
                <span>When this token signs documents, the activity will appear here.</span>
              </div>
            )}
          </div>
        </section>

        <section className={ui.panel}>
          <SectionHeading tone="green" icon={<FileCheckIcon />} title="Processed Files" />
          {report.events.map(event => {
            const heading = event.folder_name || event.file_name || event.mode;
            return (
              <div className={styles.eventBlock} key={`${event.event_id}-files`}>
                <strong title={heading}>{heading}</strong>
                {event.files.map((file, index) => {
                  const fileName = file.name || file.filename || 'Unnamed file';
                  return (
                    <div className={styles.fileRow} key={`${event.event_id}-${index}`}>
                      <span title={fileName}>{fileName}</span>
                      <span>{file.pages || 0} pages</span>
                      <span>{file.signature_count || 0} signatures</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </section>
      </>}
    </div>
  );
}
